import React, { useCallback, useEffect, useRef, useState } from "react";
import { Shirt, Feather, Smile, MoveHorizontal } from "lucide-react";
import { ANTES_DESPUES, IMAGES } from "@/content";
import Reveal from "@/components/Reveal";

const ICONS = { Shirt, Feather, Smile };

/** Placeholder ilustrativo (silueta de perfil) mientras no haya fotos reales */
function TorsoPlaceholder({ estado }) {
  const inflamado = estado === "antes";
  const id = inflamado ? "clipAntes" : "clipDespues";

  const silueta = inflamado
    ? "M80 16 C70 62 66 116 72 166 C77 212 84 254 86 296 L152 296 C154 252 160 214 176 176 C192 136 160 62 152 16 Z"
    : "M80 16 C70 62 66 116 72 166 C77 212 84 254 86 296 L152 296 C152 252 154 214 157 176 C162 136 158 62 152 16 Z";

  return (
    <div
      className="w-full h-full flex items-center justify-center relative"
      style={{
        background: inflamado
          ? "linear-gradient(160deg, #F7EDF1 0%, #E9D9E0 100%)"
          : "linear-gradient(160deg, #F4FAE8 0%, #DFEFC9 100%)",
      }}
    >
      <svg viewBox="0 0 240 310" className="h-[88%] w-auto" aria-hidden="true">
        <defs>
          <clipPath id={id}>
            <path d={silueta} />
          </clipPath>
          <linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={inflamado ? "#D3B0BD" : "#B7CE8E"} />
            <stop offset="100%" stopColor={inflamado ? "#B78D9D" : "#9CBC6C"} />
          </linearGradient>
        </defs>

        <path d={silueta} fill={`url(#${id}-g)`} />

        {/* ropa deportiva clara */}
        <g clipPath={`url(#${id})`}>
          <rect x="40" y="198" width="170" height="58" fill="#FFFFFF" opacity="0.9" />
          <rect x="40" y="196" width="170" height="5" fill="#FFFFFF" />
        </g>

        {/* l\u00ednea de referencia del contorno */}
        <path
          d={silueta}
          fill="none"
          stroke={inflamado ? "#8C5F71" : "#6E8F3E"}
          strokeWidth="2"
          opacity="0.35"
        />
      </svg>

      <span
        className="absolute text-[10px] font-semibold px-2 py-1 rounded-full"
        style={{
          bottom: 52,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(255,255,255,0.85)",
          color: "var(--gris)",
          letterSpacing: "0.06em",
          whiteSpace: "nowrap",
        }}
      >
        IMAGEN ILUSTRATIVA
      </span>
    </div>
  );
}

export default function AntesDespues() {
  const [pos, setPos] = useState(74);
  const boxRef = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = boxRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(2, Math.min(98, p)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      update(x);
    };
    const up = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [update]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(2, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(98, p + 4));
  };

  return (
    <section
      className="py-16 md:py-24"
      style={{ background: "var(--crema)" }}
      data-testid="section-antes-despues"
    >
      <div className="container-page">
        <Reveal className="text-center">
          <h2 className="h2-display" style={{ color: "var(--vino)" }}>
            Así se siente el{" "}
            <span style={{ color: "var(--lima)" }}>día 6</span>
          </h2>
          <p className="mt-3" style={{ color: "var(--gris)", fontSize: 16 }}>
            {ANTES_DESPUES.sub}
          </p>
        </Reveal>

        <Reveal delay={80} className="mt-9 mx-auto" style={{ maxWidth: 520 }}>
          <div
            ref={boxRef}
            className="relative overflow-hidden select-none"
            style={{
              aspectRatio: "900 / 860",
              borderRadius: 24,
              boxShadow: "var(--shadow-mid)",
              background: "var(--blanco)",
              cursor: "ew-resize",
            }}
            onMouseDown={(e) => {
              dragging.current = true;
              update(e.clientX);
            }}
            onTouchStart={(e) => {
              dragging.current = true;
              update(e.touches[0].clientX);
            }}
            data-testid="slider-antes-despues"
          >
            {/* DESPUÉS (capa de fondo) */}
            <div className="absolute inset-0">
              {IMAGES.despues ? (
                <img
                  src={IMAGES.despues}
                  alt="Abdomen después del método, con menos hinchazón"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <TorsoPlaceholder estado="despues" />
              )}
            </div>

            {/* ANTES (capa recortada) */}
            <div
              className="absolute inset-0"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            >
              {IMAGES.antes ? (
                <img
                  src={IMAGES.antes}
                  alt="Abdomen antes del método, con hinchazón"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <TorsoPlaceholder estado="antes" />
              )}
            </div>

            {/* etiquetas */}
            <span
              className="absolute rounded-full text-white text-[11px] font-semibold px-3 py-1.5"
              style={{
                top: 14,
                left: 14,
                background: "var(--vino)",
                letterSpacing: "0.08em",
                zIndex: 3,
              }}
            >
              {ANTES_DESPUES.labelAntes}
            </span>
            <span
              className="absolute rounded-full text-[11px] font-semibold px-3 py-1.5"
              style={{
                top: 14,
                right: 14,
                background: "var(--lima)",
                color: "#31490f",
                letterSpacing: "0.08em",
                zIndex: 3,
              }}
            >
              {ANTES_DESPUES.labelDespues}
            </span>

            {/* barra + manija */}
            <div
              className="absolute top-0 bottom-0"
              style={{ left: `${pos}%`, width: 3, background: "#fff", zIndex: 4 }}
              aria-hidden="true"
            />
            <button
              type="button"
              role="slider"
              aria-label="Comparar antes y después"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(pos)}
              onKeyDown={onKey}
              onMouseDown={(e) => {
                e.stopPropagation();
                dragging.current = true;
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                dragging.current = true;
              }}
              className="absolute flex items-center justify-center rounded-full"
              style={{
                left: `${pos}%`,
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: 46,
                height: 46,
                background: "#fff",
                border: "none",
                boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
                zIndex: 5,
                cursor: "grab",
              }}
              data-testid="slider-handle"
            >
              <MoveHorizontal size={20} strokeWidth={2} style={{ color: "var(--vino)" }} />
            </button>

            {/* pista de arrastre */}
            <span
              className="absolute text-[11px] px-3 py-1 rounded-full"
              style={{
                bottom: 14,
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(43,36,48,0.7)",
                color: "#fff",
                zIndex: 4,
              }}
            >
              {ANTES_DESPUES.hint}
            </span>
          </div>

          <p
            className="mt-4 text-[12px] leading-relaxed text-center mx-auto"
            style={{ color: "var(--gris)", maxWidth: 480 }}
          >
            {ANTES_DESPUES.aviso}
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {ANTES_DESPUES.cambios.map((c, i) => {
            const Icon = ICONS[c.icon] || Shirt;
            return (
              <Reveal
                key={c.text}
                delay={i * 80}
                className="flex items-center gap-3 p-4"
                style={{
                  background: "var(--blanco)",
                  borderRadius: 16,
                  boxShadow: "var(--shadow-soft)",
                }}
              >
                <span
                  className="flex items-center justify-center rounded-full shrink-0"
                  style={{ width: 42, height: 42, background: "var(--lima-soft)" }}
                >
                  <Icon size={20} strokeWidth={2} style={{ color: "var(--vino)" }} />
                </span>
                <span
                  className="text-[15px] leading-snug font-medium"
                  style={{ color: "var(--carbon)" }}
                >
                  {c.text}
                </span>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
